/**
 * Community over the wire — `docs/plans/community.md`, `/community/*` on qv-data.
 *
 * `api/tracklists.ts`'s shape and its reason: these are rows people wrote, one
 * action per call, acknowledged. Every call **throws** with the server's own
 * sentence ("the limit here is 10 posts an hour"), which the panel shows as is.
 *
 * Image URLs come back as a path (`/community/img/<token>`) when the image is on
 * qv-data's volume, or absolute when it is in S3; `imageSrc` makes either one
 * loadable from the page.
 */

import { BRAND } from "../lib/brand-current";
import { authHeaders } from "../lib/session";
import { currentUserId, requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

/**
 * Community's own app (`qf-community`, D57) when the build names one, else
 * qv-data's — the dev proxy, and every build from before the split.
 */
const BASE = httpBase(
  import.meta.env.VITE_QV_COMMUNITY_URL as string | undefined,
  import.meta.env.VITE_QV_SET_URL as string | undefined,
);

export type CommunityRole = "admin" | "host" | "member" | "visitor" | "outsider";
export type ThreadSort = "new" | "hot" | "top";

export interface Topic {
  id: string;
  name: string;
  colour: string;
  description: string;
  postPolicy: "members" | "hosts";
  /** Who may reply: everyone, Hosts and admins only, or nobody (a read-only topic). */
  replyPolicy?: "everyone" | "hosts" | "off";
  isOpen: boolean;
  order: number;
  archived: boolean;
}

export interface Space {
  orgKey: string;
  name: string;
  role: CommunityRole;
  enabled: boolean;
  rules: string;
  rateLimit: number;
  openHouse: boolean;
  openDescription: string;
  brand: string | null;
  hosts: { name: string; userKey?: string }[];
  admins: string[];
  topics?: Topic[];
  queue?: { approvals: number; reports: number };
}

/** One attachment. A copy of an app object, never a link into someone's workspace. */
export type Embed =
  | {
      kind: "chart";
      snapshotId?: string;
      imageUrl?: string;
      symbol: string;
      interval?: string;
      indicators?: string[];
      /** The `ChartGroup`, minus nothing — `Open live` re-mints its ids. */
      group?: Record<string, unknown>;
      takenAt?: string;
    }
  | {
      kind: "layout";
      name: string;
      layout: Record<string, unknown>;
      symbols?: string[];
      snapshotIds?: string[];
      imageUrls?: string[];
    }
  | {
      kind: "screen";
      name: string;
      config: Record<string, unknown>;
      count?: number;
    }
  | {
      kind: "plan";
      symbol: string;
      direction: "long" | "short";
      entry: number;
      stop: number;
      targets: number[];
      entryOrder?: string;
      frozenAt: string;
    }
  | { kind: "tracklist"; name: string; symbols: string[] };

export interface Post {
  id: string;
  topicId: string | null;
  parentId?: string | null;
  threadId?: string;
  depth: number;
  author: string;
  authorBadge: "admin" | "host" | "visitor" | null;
  authorKey?: string;
  isMine: boolean;
  title: string;
  body: string;
  embeds: Embed[];
  status: "published" | "pending" | "rejected" | "removed" | "expired";
  pinned: boolean;
  /** When the pin lapses; null is "until someone unpins it". */
  pinnedUntil?: string | null;
  locked: boolean;
  score: number;
  voted: boolean;
  commentCount: number;
  createdAt: string | null;
  editedAt: string | null;
  lastActivityAt: string | null;
  pendingEdit?: boolean;
  pending?: { title: string | null; body: string | null; embeds: Embed[] };
  rejectReason?: string | null;
  history?: { approved: number; rejected: number };
  kindOfWait?: "new" | "edit";
}

export interface DiscoverEntry {
  orgKey: string;
  name: string;
  description: string;
  members: number;
  visitors: number;
  openTopics: string[];
  isMember: boolean;
  joined: boolean;
}

export interface Notification {
  id: number;
  orgKey: string;
  group: string;
  kind: "reply" | "approved" | "rejected" | "removed" | "pending" | "report";
  text: string;
  postId: string | null;
  threadId: string | null;
  createdAt: string | null;
  read: boolean;
}

export interface Report {
  id: number;
  reason: string;
  note: string;
  reporter: string;
  createdAt: string | null;
  post: Post;
}

export function communityEnabled(): boolean {
  return BASE !== "";
}

/** A path from the server, made loadable; an absolute S3 URL passes through. */
export function imageSrc(url: string | undefined): string | undefined {
  if (!url) return undefined;
  return url.startsWith("/") ? `${BASE}${url}` : url;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  if (!BASE) throw new Error("no community server configured");
  const separator = path.includes("?") ? "&" : "?";
  const url = `${BASE}${path}${separator}userId=${encodeURIComponent(currentUserId())}`;
  const auth = await requestHeaders();
  const response = await fetch(url, {
    ...init,
    headers: { "content-type": "application/json", ...auth, ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail ?? `community ${response.status}`);
  }
  return (await response.json()) as T;
}

const post = <T>(path: string, body: unknown = {}): Promise<T> =>
  call<T>(path, { method: "POST", body: JSON.stringify(body) });

const org = (key: string) => `/community/${encodeURIComponent(key)}`;
const brand = () => `brand=${encodeURIComponent(BRAND.key)}`;

export const fetchMySpaces = () =>
  call<{ spaces: Space[]; unread: number }>(`/community/me?${brand()}`);
export const fetchDiscover = () => call<DiscoverEntry[]>(`/community/discover?${brand()}`);
export const fetchSpace = (orgKey: string) => call<Space>(org(orgKey));
export const saveSpaceSettings = (orgKey: string, patch: Partial<Space>) =>
  post<Space>(`${org(orgKey)}/settings?${brand()}`, patch);
export const fetchMembers = (orgKey: string) =>
  call<{ userKey: string; name: string; role: string; host: boolean }[]>(`${org(orgKey)}/members`);
export const setHost = (orgKey: string, userKey: string, on: boolean) =>
  post<{ hosts: number; warn: boolean }>(`${org(orgKey)}/hosts`, { userKey, on });
export const joinOpenHouse = (orgKey: string, on: boolean) =>
  post<{ orgKey: string; joined: boolean }>(`${org(orgKey)}/join`, { on });

export const saveTopic = (orgKey: string, topicId: string | null, patch: Partial<Topic>) =>
  post<Topic>(topicId ? `${org(orgKey)}/topics/${encodeURIComponent(topicId)}` : `${org(orgKey)}/topics`, patch);
export const reorderTopics = (orgKey: string, ids: string[]) =>
  post<Topic[]>(`${org(orgKey)}/topics/reorder`, { ids });

export const fetchThreads = (
  orgKey: string,
  opts: { topic?: string | null; sort: ThreadSort; offset?: number; limit?: number },
) => {
  const params = new URLSearchParams({ sort: opts.sort, offset: String(opts.offset ?? 0), limit: String(opts.limit ?? 30) });
  if (opts.topic) params.set("topic", opts.topic);
  return call<{ role: CommunityRole; threads: Post[]; hasMore: boolean }>(`${org(orgKey)}/threads?${params}`);
};
export const fetchThread = (orgKey: string, threadId: string) =>
  call<{ role: CommunityRole; thread: Post; comments: Post[] }>(
    `${org(orgKey)}/threads/${encodeURIComponent(threadId)}`,
  );
export const fetchThreadText = (orgKey: string, threadId: string) =>
  call<{ title: string; text: string }>(`${org(orgKey)}/threads/${encodeURIComponent(threadId)}/text`);

export interface NewPost {
  topicId?: string;
  parentId?: string;
  title?: string;
  body: string;
  embeds?: Embed[];
}

export const createPost = (orgKey: string, input: NewPost) => post<Post>(`${org(orgKey)}/posts`, input);
export const editPost = (orgKey: string, postId: string, input: Partial<NewPost>) =>
  post<Post>(`${org(orgKey)}/posts/${encodeURIComponent(postId)}`, input);
export const deletePost = (orgKey: string, postId: string) =>
  call<{ id: string; status: string }>(`${org(orgKey)}/posts/${encodeURIComponent(postId)}`, { method: "DELETE" });
export const votePost = (orgKey: string, postId: string) =>
  post<{ id: string; score: number; voted: boolean }>(`${org(orgKey)}/posts/${encodeURIComponent(postId)}/vote`);
export const reportPost = (orgKey: string, postId: string, reason: string, note = "") =>
  post<{ reported: boolean }>(`${org(orgKey)}/posts/${encodeURIComponent(postId)}/report`, { reason, note });

export type Decision = "approve" | "reject" | "remove" | "pin" | "unpin" | "lock" | "unlock" | "dismiss";
/** `days` only means something with `pin`: the pin lapses after that many days; absent is no expiry. */
export const decide = (orgKey: string, postId: string, action: Decision, reason = "", days?: number) =>
  post<{ id: string; status: string; pinned: boolean; pinnedUntil: string | null; locked: boolean }>(
    `${org(orgKey)}/posts/${encodeURIComponent(postId)}/decide`,
    days === undefined ? { action, reason } : { action, reason, days },
  );
export const banUser = (orgKey: string, userKey: string, on: boolean) =>
  post<{ banned: boolean }>(`${org(orgKey)}/ban`, { userKey, on });
export const fetchQueue = (orgKey: string) =>
  call<{ approvals: Post[]; reports: Report[] }>(`${org(orgKey)}/queue`);

export const uploadSnapshot = (orgKey: string, pngBase64: string, definition: Record<string, unknown>) =>
  post<{ snapshotId: string; width: number; height: number; imageUrl: string }>(
    `${org(orgKey)}/snapshots`,
    { pngBase64, definition },
  );

export const fetchNotifications = () =>
  call<{ unread: number; items: Notification[] }>("/community/notifications");
export const markNotificationsRead = (ids?: number[]) =>
  post<{ ok: boolean }>("/community/notifications/read", ids ? { ids } : {});

// ── QueVee: a thread, summarised ─────────────────────────────────────────────

const QUEVEE_URL = (import.meta.env.VITE_QUEVEE_URL as string | undefined) ?? "";

export function summariesEnabled(): boolean {
  return QUEVEE_URL !== "";
}

/**
 * QueVee's summary of one thread. The service reads the thread from qv-data as
 * this reader, so it summarises exactly what they may see. `ok: false` carries a
 * sentence to show, never a throw — the analyst route's shape.
 */
export async function summariseThread(
  orgKey: string,
  threadId: string,
): Promise<{ ok: true; summary: string } | { ok: false; error: { message: string } }> {
  if (!QUEVEE_URL) return { ok: false, error: { message: "Summaries are not configured here." } };
  const response = await fetch(`${QUEVEE_URL.replace(/\/$/, "")}/community/summarise`, {
    method: "POST",
    headers: { "content-type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({ orgKey, threadId }),
  });
  if (response.status === 429) return { ok: false, error: { message: "You've used today's summaries." } };
  if (response.status === 403) return { ok: false, error: { message: "Summaries are not in your plan." } };
  if (!response.ok) return { ok: false, error: { message: "Summaries are temporarily unavailable." } };
  return (await response.json()) as { ok: true; summary: string } | { ok: false; error: { message: string } };
}
